import React, { useRef, useEffect, useMemo, useCallback, useState } from "react";
import { Container, Graphics } from "@pixi/react";
import { useSelector } from "react-redux";
import anime from "animejs";
import { useReelRefs } from "../../../Contexts/ReelRefsContext";
import { useDimensions } from "../../../Contexts/DimensionsContext";
import InfoOverlayController from "./InfoOverlayController";
import {
  selectIsSpinning,
  selectSpinComplete,
  selectHoldAndWinActive,
  selectFreeSpinActive,
  selectAutoPlayActive,
  selectBet,
  selectWinAnimationsPlaying,
  selectSymbolPopupActive,
  selectFreeRoundsActive,
  selectFreeRoundsBet,
  selectInfoOverlayPopupActive,
  selectAnyPopupActive,
  selectMenuActive,
} from "../../../State/selectors";
import { selectBaseBet } from "../../../State/Slices/gameSettingsSlice";
import { useTranslation } from "react-i18next";
import { FEATURES_CONFIG } from "../../../featuresConfig";

// Overlay styling — matches WarningPopup exactly
const OVERLAY_COLOR = 0x090f41;
const OVERLAY_ALPHA = 0.6;
const OVERLAY_FADE_DURATION = 200;

/**
 * InfoOverlay - React wrapper component for InfoOverlayController
 * Manages controller lifecycle, dimensions updates, bet updates, and blocking conditions
 * Prevents popup interaction during spin, autoplay, Hold & Win, or win animations
 */
const InfoOverlay = () => {
  const { reelRefs, overlayReelRefs } = useReelRefs();
  const fullDimensions = useDimensions(); // Full context for controller
  const { t } = useTranslation();

  const isSpinning = useSelector(selectIsSpinning);
  const spinComplete = useSelector(selectSpinComplete);
  const holdAndWinActive = useSelector(selectHoldAndWinActive);
  const freeSpinActive = useSelector(selectFreeSpinActive);
  const autoPlayActive = useSelector(selectAutoPlayActive);

  // Use free rounds bet when active, otherwise use regular bet - memoized
  const freeRoundsActive = useSelector(selectFreeRoundsActive);
  const freeRoundsBet = useSelector(selectFreeRoundsBet);
  const regularBet = useSelector(selectBet);
  const bet = useMemo(
    () =>
      FEATURES_CONFIG.FREE_ROUNDS.ENABLED && freeRoundsActive && freeRoundsBet
        ? freeRoundsBet
        : regularBet,
    [freeRoundsActive, freeRoundsBet, regularBet]
  );

  const baseBet = useSelector(selectBaseBet);
  const winAnimationsPlaying = useSelector(selectWinAnimationsPlaying);

  const overlayContainerRef = useRef(null);
  const controllerRef = useRef(null);
  const overlayAnimRef = useRef(null);
  const [overlayAlpha, setOverlayAlpha] = useState(0);
  const symbolPopupActive = useSelector(selectSymbolPopupActive);
  const infoOverlayPopupActive = useSelector(selectInfoOverlayPopupActive);
  const anyPopupActive = useSelector(selectAnyPopupActive);
  const menuActive = useSelector(selectMenuActive);

  // Memoize container interactive state
  const containerInteractive = useMemo(
    () =>
      !isSpinning &&
      spinComplete &&
      !holdAndWinActive &&
      !freeSpinActive &&
      !autoPlayActive &&
      !winAnimationsPlaying,
    [
      isSpinning,
      spinComplete,
      holdAndWinActive,
      freeSpinActive,
      autoPlayActive,
      winAnimationsPlaying,
    ]
  );

  // Initialize controller
  useEffect(() => {
    if (overlayContainerRef.current && !controllerRef.current) {
      controllerRef.current = new InfoOverlayController(
        overlayContainerRef.current,
        fullDimensions,
        reelRefs,
        t,
        bet,
        overlayReelRefs
      );
    }

    return () => {
      if (controllerRef.current) {
        controllerRef.current.destroy();
        controllerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update dimensions on resize
  useEffect(() => {
    if (controllerRef.current) {
      controllerRef.current.resize(fullDimensions);
    }
  }, [fullDimensions]);

  // Update bet when it changes
  useEffect(() => {
    if (controllerRef.current) {
      controllerRef.current.updateBet(bet);
    }
  }, [bet]);

  // Update base bet when it changes
  useEffect(() => {
    if (controllerRef.current) {
      controllerRef.current.updateBaseBet(baseBet);
    }
  }, [baseBet]);

  // Close popup when blocking conditions become active
  useEffect(() => {
    if (!controllerRef.current) return;

    const hasActivePopup = controllerRef.current.hasActivePopup();

    // Close popup if it's active and any blocking condition is true
    if (
      hasActivePopup &&
      (isSpinning || holdAndWinActive || freeSpinActive || autoPlayActive || winAnimationsPlaying)
    ) {
      controllerRef.current.closePopup();
    }
  }, [isSpinning, holdAndWinActive, freeSpinActive, autoPlayActive, winAnimationsPlaying]);

  // Close popup when any other popup becomes active or menu is active
  useEffect(() => {
    if (!controllerRef.current) return;

    const hasActivePopup = controllerRef.current.hasActivePopup();

    // Close InfoOverlay popup if it's active and any other popup is open or menu is active
    if (hasActivePopup && (anyPopupActive || menuActive)) {
      controllerRef.current.closePopup();
    }
  }, [anyPopupActive, menuActive]);

  // Update interactive state based on all blocking conditions
  useEffect(() => {
    if (controllerRef.current) {
      controllerRef.current.setInteractive(containerInteractive);
    }
  }, [containerInteractive]);

  // Close popup when Redux state changes to false (triggered by background click)
  useEffect(() => {
    if (controllerRef.current && !infoOverlayPopupActive) {
      const hasActivePopup = controllerRef.current.hasActivePopup();
      if (hasActivePopup) {
        controllerRef.current.closePopup();
      }
    }
  }, [infoOverlayPopupActive]);

  // Animate overlay fade in/out — matches WarningPopup animation exactly
  useEffect(() => {
    // Cancel any running animation
    if (overlayAnimRef.current) {
      anime.remove(overlayAnimRef.current);
    }

    if (symbolPopupActive) {
      // Fade in
      const proxy = { alpha: overlayAlpha };
      overlayAnimRef.current = proxy;
      anime({
        targets: proxy,
        alpha: OVERLAY_ALPHA,
        duration: OVERLAY_FADE_DURATION,
        easing: "easeOutQuad",
        update: () => {
          setOverlayAlpha(proxy.alpha);
        },
      });
    } else {
      // Fade out
      const proxy = { alpha: overlayAlpha };
      overlayAnimRef.current = proxy;
      anime({
        targets: proxy,
        alpha: 0,
        duration: OVERLAY_FADE_DURATION,
        easing: "easeInQuad",
        update: () => {
          setOverlayAlpha(proxy.alpha);
        },
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [symbolPopupActive]);

  // Draw fullscreen overlay background (always mounted, alpha controlled by animation)
  const drawOverlay = useCallback((g) => {
    g.clear();
    g.beginFill(OVERLAY_COLOR);
    // Draw a very large rect centered at origin to cover entire viewport
    g.drawRect(-5000, -5000, 10000, 10000);
    g.endFill();
  }, []);

  // Handle overlay click — close popup
  const handleOverlayClick = useCallback(() => {
    if (controllerRef.current) {
      controllerRef.current.closePopup();
    }
  }, []);

  return (
    <Container
      name="info_overlay"
      zIndex={13}
      eventMode={containerInteractive ? "static" : "none"}
      sortableChildren={true}
    >
      {/* Fullscreen dark overlay — always mounted, alpha animated */}
      <Graphics
        draw={drawOverlay}
        zIndex={1}
        alpha={overlayAlpha}
        eventMode={symbolPopupActive ? "static" : "none"}
        cursor="pointer"
        pointerdown={handleOverlayClick}
      />

      <Container
        ref={overlayContainerRef}
        name="info_overlay_container"
        sortableChildren
        zIndex={2}
      />
    </Container>
  );
};

export default InfoOverlay;
